import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import {
  api,
  type CarrierInitializationDiscoveryReview,
  type CarrierInitializationDiscoveryState,
} from '@/services/api';
import { cn } from '@/lib/utils';
import { CarrierDiscoveryReview } from './CarrierDiscoveryReview';
import { CarrierMappingDrafts } from './CarrierMappingDrafts';
import { getCarrierDiscoveryPresentation } from './carrierDiscoveryProgressState';
import {
  MAX_DISCOVERY_STEPS_PER_ACTION,
  runCarrierDiscovery,
} from './carrierDiscoveryRunner';

interface CarrierDiscoveryProgressProps {
  disabled?: boolean;
  // Start or continue discovery once when mounted (the setup overview's action).
  autoStart?: boolean;
  defaultOrganizerOpen?: boolean;
  organizerOpenKey?: string;
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'The Carrier discovery state could not be loaded.';
}

export function CarrierDiscoveryProgress({
  disabled = false,
  autoStart = false,
  defaultOrganizerOpen = false,
  organizerOpenKey,
}: CarrierDiscoveryProgressProps) {
  const [state, setState] = useState<CarrierInitializationDiscoveryState | null>(null);
  const [review, setReview] = useState<CarrierInitializationDiscoveryReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadReview = useCallback(async () => {
    setReviewLoading(true);
    setReviewError(null);
    try {
      setReview(await api.carrierInitialization.getDiscoveryReview());
    } catch (loadError) {
      setReviewError(errorMessage(loadError));
    } finally {
      setReviewLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const storedState = await api.carrierInitialization.getDiscovery();
      setState(storedState);
      if (storedState) {
        await loadReview();
      } else {
        setReview(null);
      }
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [loadReview]);

  useEffect(() => {
    void load();
  }, [load]);

  const advance = async () => {
    if (disabled || loading || advancing) return;
    setAdvancing(true);
    setError(null);
    try {
      const result = await runCarrierDiscovery({
        advance: () => api.carrierInitialization.advanceDiscovery(),
        onProgress: async (nextState) => {
          setState(nextState);
          await loadReview();
        },
        shouldContinue: () => mountedRef.current,
      });
      if (result.stopReason === 'step_limit') {
        setError(
          `Discovery paused after ${MAX_DISCOVERY_STEPS_PER_ACTION} steps. Your progress is saved. Continue to resume.`
        );
      } else if (result.stopReason === 'stalled') {
        setError('Discovery paused because the next step was unavailable. Your progress is saved.');
      }
    } catch (advanceError) {
      setError(errorMessage(advanceError));
    } finally {
      if (mountedRef.current) {
        setAdvancing(false);
      }
    }
  };

  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStartedRef.current || loading || state?.progress.complete) return;
    autoStartedRef.current = true;
    void advance();
  // advance reads current state; this runs once after the first load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, loading, state]);

  const progress = state?.progress ?? null;
  const presentation = getCarrierDiscoveryPresentation(progress);

  return (
    <section
      aria-label="Carrier discovery progress"
      className="border-t border-emerald-200 bg-white/70 px-4 py-3.5 dark:border-emerald-900/70 dark:bg-surface-950/30"
    >
      <div className="flex items-start gap-3">
        <span className={cn(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          progress?.complete
            ? 'bg-emerald-600 text-white dark:bg-emerald-500'
            : 'bg-primary-100 text-primary-700 dark:bg-primary-950 dark:text-primary-300'
        )}>
          {progress?.complete
            ? <CheckCircle2 className="h-4 w-4" />
            : <Search className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1" aria-live="polite">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-surface-700 dark:text-surface-200">
                Carrier setup
              </p>
              <p className="mt-1 text-sm font-semibold text-surface-900 dark:text-surface-100">
                {loading ? 'Loading saved progress…' : presentation.phaseLabel}
              </p>
            </div>
          </div>

          {!loading && (
            <p className="mt-1 text-xs leading-5 text-surface-600 dark:text-surface-300">
              {presentation.description}
            </p>
          )}

          {reviewLoading && !review && (
            <p className="mt-3 flex items-center gap-2 text-xs text-surface-500 dark:text-surface-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading stored discovery records…
            </p>
          )}
          {review && (
            <CarrierDiscoveryReview review={review} />
          )}
          {progress?.complete && (
            <CarrierMappingDrafts
              disabled={disabled}
              defaultOpen={defaultOrganizerOpen}
              defaultOpenKey={organizerOpenKey}
            />
          )}
          {reviewError && (
            <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200" role="alert">
              <span>{reviewError}</span>
              <button type="button" onClick={() => void loadReview()} className="shrink-0 font-semibold underline underline-offset-2">
                Retry
              </button>
            </div>
          )}

          {error && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300" role="alert">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && (
            <details className="mt-3 text-xs text-surface-500 dark:text-surface-400">
              <summary className="w-fit cursor-pointer select-none font-medium hover:text-surface-700 dark:hover:text-surface-200">
                Details
              </summary>
              <div className="mt-2 rounded-lg bg-surface-50 p-3 dark:bg-surface-900">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                  <div><dt className="font-medium">Phase</dt><dd>{presentation.phaseLabel}</dd></div>
                  <div><dt className="font-medium">Status</dt><dd>{presentation.statusLabel}</dd></div>
                  {progress && (
                    <>
                      <div><dt className="font-medium">Accounts</dt><dd>{progress.counts.accounts}</dd></div>
                      <div><dt className="font-medium">Carriers</dt><dd>{progress.counts.vendors}</dd></div>
                      <div><dt className="font-medium">Relationships</dt><dd>{progress.counts.assignmentEvidence}</dd></div>
                      <div><dt className="font-medium">Unresolved</dt><dd>{progress.counts.unresolvedAssignmentEvidence}</dd></div>
                    </>
                  )}
                </dl>
                <p className="mt-2 leading-5">
                  One action continues the read-only search. Each completed step is saved. Reloading only restores saved progress.
                </p>
              </div>
            </details>
          )}

          <div className="mt-3 flex justify-end">
            {error && !state ? (
              <button
                type="button"
                disabled={loading}
                onClick={() => void load()}
                className="flex h-8 items-center gap-2 rounded-lg border border-surface-300 px-3 text-xs font-semibold text-surface-700 hover:bg-surface-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-surface-700 dark:text-surface-200 dark:hover:bg-surface-900"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                Retry
              </button>
            ) : presentation.actionLabel ? (
              <button
                type="button"
                disabled={disabled || loading || advancing}
                onClick={() => void advance()}
                className="flex h-8 items-center gap-2 rounded-lg bg-surface-900 px-3 text-xs font-semibold text-white hover:bg-surface-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-surface-950 dark:hover:bg-surface-200"
              >
                {advancing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {advancing ? 'Finding carrier data…' : presentation.actionLabel}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
